import { Component, EventEmitter, Input, OnInit, Output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CoreService } from '../../../services/core.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

type PostType = 'video' | 'carousel';

@Component({
  selector: 'app-tik-tok-dashboard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule],
  templateUrl: './tik-tok-dashboard.component.html',
  styleUrl: './tik-tok-dashboard.component.scss'
})
export class TikTokDashboardComponent implements OnInit {
  @Input() companyID = 0;
  @Input() siteID    = 0;
  @Input() formData: any = {};
  @Output() submitted = new EventEmitter<any>();

  uploadForm: FormGroup;

  // ── Post type ─────────────────────────────────────────────────────────────
  postTypes: { id: PostType; label: string, disabled?: boolean}[] = [
      { id: 'video',    label: '🎬 Video' },
      { id: 'carousel', label: '🎠 Carousel' },
  ];
  selectedPostType = signal<PostType | null>(null);

  // ── Platform ──────────────────────────────────────────────────────────────
  tiktokPlatforms  = signal<{ id: number; social_media_id: string; ms_contentsite_id: number }[]>([]);
  platformId       = signal<number | null>(null); // ← ONLY the signal, no getter
  platformsLoading = signal(false);

  // ── File ──────────────────────────────────────────────────────────────────
  selectedFile = signal<File | null>(null);
  selectedFiles = signal<File[]>([]);
  dragOver     = signal(false);
  fileIcon     = signal('description');

  // ── Upload state ──────────────────────────────────────────────────────────
  uploading  = signal(false);
  uploadDone = signal(false);
  publishId  = signal<string | null>(null);

  // ── Analytics modal ───────────────────────────────────────────────────────
  showAnalytics         = signal(false);
  // analyticsTab          = signal<'page' | 'video'>('page');
  analyticsTab          = signal<'page' | 'video' | 'summary'>('page');
  pageMetrics           = signal<{ name: string; followers: number; likes: number, video_count: number } | null>(null);
  pageAnalyticsLoading  = signal(false);
  videoStats            = signal<any[]>([]);
  videoAnalyticsLoading = signal(false);
  videoIdsInput         = signal('');
  accountSummary        = signal<any | null>(null);
  accountSummaryLoading = signal(false);
  private _objectUrls: string[] = [];

  parsedVideoIds = computed(() =>
    this.videoIdsInput().split(/[\n,]+/).map(s => s.trim()).filter(Boolean).slice(0, 20)
  );

  constructor(private fb: FormBuilder, private coreService: CoreService) {
    this.uploadForm = this.fb.group({
      caption: [''],
      description: ['']
    });
  }

  ngOnInit(): void {
    this.companyID = this.companyID || this.coreService.user?.companyID || 1;
    this.siteID    = this.siteID    || this.coreService.user?.siteID    || 45;
    console.log('Full user object:', this.coreService.user);
    console.log('companyID:', this.companyID, 'siteID:', this.siteID);
    this.loadPlatforms();
  }

  // ── Platforms ─────────────────────────────────────────────────────────────
  loadPlatforms(): void {
    this.platformsLoading.set(true);

    this.coreService.getData('/tiktok/platforms', { company_id: this.companyID }).subscribe({
      next: (platforms: any[]) => {
        this.platformsLoading.set(false);
        this.tiktokPlatforms.set(platforms);
        console.log('platforms loaded:', platforms); // ← add this
        console.log('companyID used:', this.companyID); // ← and this
        if (platforms.length === 1) {
          this.platformId.set(platforms[0].id);
        }
      },
      error: () => {
        this.platformsLoading.set(false);
        this.coreService.showSnackbar('Could not load TikTok accounts.');
      }
    });
  }

  selectPlatform(event: Event): void {
    const val = +(event.target as HTMLSelectElement).value || null;
    this.platformId.set(val);
  }

  // ── Post type ─────────────────────────────────────────────────────────────
  selectPostType(event: Event): void {
    const val = (event.target as HTMLSelectElement).value as PostType | '';
    this.selectedPostType.set(val || null);
    this.clearFile();
    this.uploadDone.set(false);
    this.publishId.set(null);
  }

  get acceptedFileTypes(): string {
    switch (this.selectedPostType()) {
      case 'video':    return 'video/mp4,video/quicktime';
      case 'carousel': return 'image/jpeg,image/webp';  // no PNG!
      default:         return '*/*';
    }
  }

  // ── File handling ─────────────────────────────────────────────────────────
  onFileSelected(event: Event): void {
      const files = Array.from((event.target as HTMLInputElement).files ?? []);
      if (!files.length) return;

      if (this.selectedPostType() === 'carousel') {
          this.selectedFiles.set([...this.selectedFiles(), ...files]);
      } else {
          this.setFile(files[0]);
      }
  }

  onDrop(event: DragEvent): void {
      event.preventDefault();
      this.dragOver.set(false);
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (!files.length) return;

      if (this.selectedPostType() === 'carousel') {
          this.selectedFiles.set([...this.selectedFiles(), ...files]);
      } else {
          this.setFile(files[0]);
      }
  }

  removeCarouselFile(index: number): void {
    const current = this.selectedFiles();
    this.selectedFiles.set(current.filter((_, i) => i !== index));
  }

  private setFile(file: File): void {
    this.selectedFile.set(file);
    const ext = file.name.split('.').pop()?.toLowerCase();
    this.fileIcon.set(['mp4', 'mov'].includes(ext ?? '') ? 'videocam' : 'image');
  }

  clearFile(): void {
      this._objectUrls.forEach(u => URL.revokeObjectURL(u));
      this._objectUrls = [];
      this.selectedFile.set(null);
      this.selectedFiles.set([]);
      this.fileIcon.set('description');
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ── Upload ────────────────────────────────────────────────────────────────
  onUpload(): void {
    const postType = this.selectedPostType()!;

    if (postType === 'carousel') {
        const files = this.selectedFiles();
        if (files.length < 2) {
            this.coreService.showSnackbar('Please upload at least 2 images for a carousel post.');
            return;
        }
        if (!this.platformId()) {
            this.coreService.showSnackbar('Please select a TikTok account first.');
            return;
        }

        const form = new FormData();
        files.forEach((f, i) => form.append(`images[${i}]`, f));
        form.append('platform_id',       String(this.platformId()));
        form.append('companyID',         String(this.companyID));
        form.append('ms_contentsite_id', String(this.siteID));
        form.append('post_type',         'carousel');
        const caption = this.uploadForm.value.caption;
        if (caption) form.append('caption', caption);

        const description = this.uploadForm.value.description;
        if (description) form.append('description', description);

        this.uploading.set(true);
        this.coreService.postData('upload', form).subscribe({
            next: (res: any) => {
                this.uploading.set(false);
                if (res.success) {
                    this.uploadDone.set(true);
                    this.publishId.set(res.publish_id);
                    this.coreService.showSnackbar('Carousel sent to TikTok inbox!');
                    this.submitted.emit(res);
                } else {
                    this.coreService.showSnackbar(res.message ?? 'Upload failed.');
                }
            },
            error: (err: any) => {
                this.uploading.set(false);
                this.coreService.showSnackbar(err.error?.message ?? 'Upload failed.');
            }
        });

    } else {
        // Video
        if (!this.selectedFile()) return;
        if (!this.platformId()) {
            this.coreService.showSnackbar('Please select a TikTok account first.');
            return;
        }

        const form = new FormData();
        form.append('video',             this.selectedFile()!);
        form.append('platform_id',       String(this.platformId()));
        form.append('companyID',         String(this.companyID));
        form.append('ms_contentsite_id', String(this.siteID));
        form.append('post_type',         'video');
        const caption = this.uploadForm.value.caption;
        if (caption) form.append('caption', caption);

        this.uploading.set(true);
        this.coreService.postData('tiktok/upload', form).subscribe({
            next: (res: any) => {
                this.uploading.set(false);
                if (res.success) {
                    this.uploadDone.set(true);
                    this.publishId.set(res.publish_id);
                    this.coreService.showSnackbar('Video posted to TikTok!');
                    this.submitted.emit(res);
                } else {
                    this.coreService.showSnackbar(res.message ?? 'Upload failed.');
                }
            },
            error: (err: any) => {
                this.uploading.set(false);
                this.coreService.showSnackbar(err.error?.message ?? 'Upload failed.');
            }
        });
    }
  }

  // ── Analytics ─────────────────────────────────────────────────────────────
  openAnalytics(): void {
    this.showAnalytics.set(true);
    this.loadPageAnalytics();
  }
  closeAnalytics(): void { this.showAnalytics.set(false); }

  loadPageAnalytics(): void {
    if (!this.platformId()) return;

    this.pageAnalyticsLoading.set(true);
    this.pageMetrics.set(null);

    this.coreService.getData('/analytics/page', { platform_id: this.platformId()! }).subscribe({
      next: (res: any) => {
        this.pageAnalyticsLoading.set(false);
        this.pageMetrics.set(res.profile_metrics);
      },
      error: (err: any) => {
        this.pageAnalyticsLoading.set(false);
        this.coreService.showSnackbar(err.error?.message ?? 'Failed to load analytics.');
      }
    });
  }

  loadVideoAnalytics(): void {
    const ids = this.parsedVideoIds();
    if (!ids.length) return;

    this.videoAnalyticsLoading.set(true);
    this.videoStats.set([]);

    this.coreService.postData('tiktok/video-analytics', {
      platform_id: this.platformId(),   // ← signal call, not this.platformId
      video_ids:   ids
    }).subscribe({
      next: (res: any) => {
        this.videoAnalyticsLoading.set(false);
        this.videoStats.set(res.data ?? res.videos ?? []);
      },
      error: (err: any) => {
        this.videoAnalyticsLoading.set(false);
        this.coreService.showSnackbar(err.error?.message ?? 'Failed to load video stats.');
      }
    });
  }

  setVideoIdsInput(event: Event): void {
    this.videoIdsInput.set((event.target as HTMLTextAreaElement).value);
  }

  formatNumber(n: number): string {
    return new Intl.NumberFormat().format(n ?? 0);
  }

  connectTikTok(): void {

    if (!this.companyID || !this.siteID) {
      this.coreService.showSnackbar('Missing company or site configuration.');
      console.log(this.companyID);
      console.log("Site ID: ", this.siteID);
      return;
    }


    this.coreService.postData('tiktok/save-and-login', {
      company_id: this.companyID,
      site_id:    this.siteID,
    }).subscribe({
      next: (res) => {
        if (res.redirect_url) {
          window.location.href = res.redirect_url;
        }
      },
      error: () => {
        this.coreService.showSnackbar('Failed to connect TikTok.');
      }
    });
  }

  reconnectTikTok(): void {
    this.connectTikTok(); // same method, just call it again
  }

  loadAccountSummary(): void
  {
    if (!this.platformId()) return;

    this.accountSummaryLoading.set(true);
    this.accountSummary.set(null);

    this.coreService.getData('/tiktok/account-summary', { platform_id: this.platformId()! }).subscribe({
        next: (res: any) => {
            this.accountSummaryLoading.set(false);
            if (res.success) {
                this.accountSummary.set(res.data);
            }
        },
        error: (err: any) => {
            this.accountSummaryLoading.set(false);
            this.coreService.showSnackbar(err.error?.message ?? 'Failed to load summary.');
        }
    });
  }

  getObjectUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  getVideoUrl(): string {
    const file = this.selectedFile();
    if (!file) return '';
    const url = URL.createObjectURL(file);
    this._objectUrls.push(url);
    return url;
  }

  ngOnDestroy(): void {
      this._objectUrls.forEach(u => URL.revokeObjectURL(u));
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }
}
